import { ChainId, GasSetting } from '../../../../utils/config/networks';
import { getEVMRpcClient } from '../../../../utils/network/getRpcClient';
/**
 * Calculates the gas settings for a given set of transactions on a specified network.
 *
 * @param {Object} params - The parameters for calculating gas settings.
 * @param {ChainId} params.network - The network on which the transactions will be executed.
 * @param {Array<Transaction>} params.transactions - The list of transactions to estimate gas for.
 * @param {GasSetting} [params.gasSetting=GasSetting.MARKET] - The gas setting multiplier to apply (default is Market with 1x multiplier).
 * @returns {Promise<GasEstimation>} A promise that resolves to the gas estimation settings.
 *
 * @throws {Error} If the network is unsupported or if there is an error fetching gas settings.
 */
export async function calculateGasSettings({ network, transactions, gasSetting = GasSetting.MARKET, // Default to Market (1x multiplier)
 }) {
    var _a, _b, _c;
    const client = getEVMRpcClient({ chainId: network });
    try {
        let estimation;
        // Estimate gas for all transactions
        const gasEstimates = await Promise.all(transactions.map((tx) => client.estimateGas(Object.assign(Object.assign({}, tx), { account: tx.from }))));
        console.log('Executing order gasEstimates', gasEstimates);
        // Fetch gas data based on network type
        switch (network) {
            case ChainId.ETHEREUM:
            case ChainId.ARBITRUM:
            case ChainId.OPTIMISM:
            case ChainId.BASE:
            case ChainId.POLYGON:
            case ChainId.AVALANCHE:
                const [block, suggestedMaxPriorityFeePerGas] = await Promise.all([
                    client.getBlock(),
                    client.estimateMaxPriorityFeePerGas(),
                ]);
                const baseFeePerGas = block.baseFeePerGas;
                console.log('Executing order baseFeePerGas', baseFeePerGas);
                // Set a minimum tip (priority fee) of 2 Gwei
                const minimumTipGwei = BigInt(2 * 10 ** 9); // 2 Gwei in wei
                console.log('Executing order minimumTipGwei', minimumTipGwei);
                const maxPriorityFeePerGas = suggestedMaxPriorityFeePerGas > minimumTipGwei
                    ? suggestedMaxPriorityFeePerGas * 2n
                    : minimumTipGwei * 2n;
                console.log('Executing order maxPriorityFeePerGas', maxPriorityFeePerGas);
                // Set maxFeePerGas to be at least 2 times the base fee plus the priority fee
                const maxFeePerGas = baseFeePerGas * 2n + maxPriorityFeePerGas;
                console.log('Executing order maxFeePerGas', maxFeePerGas);
                estimation = {
                    gasPrice: maxFeePerGas, // For backwards compatibility
                    maxFeePerGas,
                    maxPriorityFeePerGas,
                    gasEstimations: gasEstimates,
                };
                break;
            case ChainId.BSC:
                const gasPrice = await client.getGasPrice();
                estimation = {
                    gasPrice,
                    gasEstimations: gasEstimates,
                };
                break;
            default:
                throw new Error(`Unsupported network: ${network}`);
        }
        // Apply the gas setting multiplier
        const multiplier = gasSetting;
        console.log('Executing order starting return Executing order', estimation);
        return {
            gasPrice: BigInt(Math.floor(Number((_a = estimation.gasPrice) === null || _a === void 0 ? void 0 : _a.toString()) * multiplier)),
            maxFeePerGas: estimation.maxFeePerGas
                ? BigInt(Math.floor(Number((_b = estimation.maxFeePerGas) === null || _b === void 0 ? void 0 : _b.toString()) * multiplier))
                : undefined,
            maxPriorityFeePerGas: estimation.maxPriorityFeePerGas
                ? BigInt(Math.floor(Number((_c = estimation.maxPriorityFeePerGas) === null || _c === void 0 ? void 0 : _c.toString()) * multiplier))
                : undefined,
            gasEstimations: estimation.gasEstimations.map((estimate) => BigInt(Math.floor(Number(estimate === null || estimate === void 0 ? void 0 : estimate.toString()) * multiplier))),
        };
    }
    catch (error) {
        console.log(`Error fetching gas settings: ${error}`);
        throw error;
    }
}
